import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, Pill, ArrowUpRight } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Badge } from "../ui/badge";
import ProductLabel from "./ProductLabel";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/products");
        if (!cancelled) setProducts(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category).filter(Boolean));
    return ["All", ...Array.from(set).sort()];
  }, [products]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const matchCat = cat === "All" || p.category === cat;
      const term = q.trim().toLowerCase();
      const matchQ = !term ||
        p.name.toLowerCase().includes(term) ||
        p.composition?.toLowerCase().includes(term) ||
        p.category?.toLowerCase().includes(term);
      return matchCat && matchQ;
    });
  }, [products, q, cat]);

  return (
    <section id="products" className="py-24 sm:py-32 px-6 sm:px-10 lg:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="section-eyebrow" data-testid="products-eyebrow">Product Portfolio</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900" data-testid="products-heading">
              Therapeutics engineered for <span className="text-gradient">real-world care</span>.
            </h2>
          </div>
          <p className="max-w-md text-slate-600" data-testid="products-subheading">
            Search our growing catalogue across dermatology, nutraceuticals, women's health,
            allergy, digestive health and more.
          </p>
        </div>

        {/* Search + filter */}
        <div className="mt-10 flex flex-col gap-4">
          <div className="relative max-w-xl">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products, composition, category…"
              className="w-full pl-11 pr-4 py-3.5 rounded-full bg-white border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm placeholder:text-slate-400"
              data-testid="product-search-input"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`product-filter-${c.toLowerCase().replace(/\s|&|\//g, "-")}`}
                className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  cat === c
                    ? "bg-blue-900 text-white border-blue-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-900"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-72 rounded-2xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-16 text-center py-16 border border-dashed border-slate-200 rounded-2xl" data-testid="products-empty">
            <Pill className="mx-auto text-slate-300" size={32} />
            <p className="mt-3 text-slate-500">No products match your search.</p>
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-6">
            {filtered.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: (i % 8) * 0.04 }}
                className="group relative bg-white rounded-2xl border border-slate-200 overflow-hidden hover:-translate-y-1.5 hover:shadow-xl hover:border-blue-200 transition-[transform,box-shadow,border-color] duration-300"
                data-testid={`product-card-${p.name.toLowerCase().replace(/\s|\//g, "-")}`}
              >
                <div className="relative h-44 overflow-hidden bg-white">
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                    />
                  ) : (
                    <div className="w-full h-full group-hover:scale-[1.03] transition-transform duration-500 ease-out">
                      <ProductLabel name={p.name} form={p.form} />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-950/25 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute top-3 right-3">
                    <span className="inline-flex items-center rounded-full bg-white/95 backdrop-blur px-2.5 py-1 text-[0.65rem] font-semibold tracking-wider uppercase text-blue-900 border border-white/70 shadow-sm">
                      {p.category}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display text-lg font-semibold text-slate-900">{p.name}</h3>
                    <span className="text-[0.65rem] font-medium tracking-wider uppercase text-slate-400">{p.form}</span>
                  </div>
                  <p className="mt-2 text-xs text-slate-500 line-clamp-2 leading-relaxed">
                    {p.composition || "Detailed composition available on request."}
                  </p>
                  <button
                    onClick={() => setSelected(p)}
                    data-testid={`product-view-${p.name.toLowerCase().replace(/\s|\//g, "-")}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-blue-900 hover:text-green-600 transition-colors"
                  >
                    View Details <ArrowUpRight size={14} />
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="product-details-dialog">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100">
                    {selected.category}
                  </Badge>
                  {selected.form && <Badge variant="outline">{selected.form}</Badge>}
                </div>
                <DialogTitle className="font-display text-2xl">{selected.name}</DialogTitle>
                <DialogDescription className="text-slate-600 leading-relaxed pt-2">
                  {selected.composition || "Composition information available upon request."}
                </DialogDescription>
              </DialogHeader>
              {selected.image_url ? (
                <img src={selected.image_url} alt={selected.name} className="mt-3 w-full h-52 object-cover rounded-xl" />
              ) : (
                <div className="mt-3 w-full h-52 rounded-xl overflow-hidden border border-slate-200">
                  <ProductLabel name={selected.name} form={selected.form} />
                </div>
              )}
              <a href="#contact" onClick={() => setSelected(null)} className="btn-primary w-full mt-4 justify-center" data-testid="product-details-cta">
                Enquire About This Product
              </a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
